import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

export type { ValueSetCode };

const formSchema = z.object({
  relationship: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  name: z.string().trim(),
  conditionCode: z.custom<ValueSetCode | undefined>(),
  notes: z.string().trim(),
});

export type AddFamilyMemberHistoryFormValues = z.infer<typeof formSchema>;

export interface AddFamilyMemberHistoryFormProps {
  onCreate: (values: AddFamilyMemberHistoryFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  relationship: UseValueSetExpandResult;
  conditionCode: UseValueSetExpandResult;
}

export function AddFamilyMemberHistoryForm({
  onCreate,
  onClose,
  isCreating,
  relationship,
  conditionCode,
}: AddFamilyMemberHistoryFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const form = useForm({
    defaultValues: {
      relationship: undefined as ValueSetCode | undefined,
      name: '',
      conditionCode: undefined as ValueSetCode | undefined,
      notes: '',
    },
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <FieldGroup>
        <form.Field name="relationship">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('familyMemberRelationship')}
              placeholder={t('familyMemberRelationshipPlaceholder')}
              items={relationship.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={relationship.onQueryChange}
              isLoading={relationship.isFetching}
              error={relationship.error}
              emptyMessage={t('noMatchingResults')}
              required
            />
          )}
        </form.Field>

        <form.Field name="name">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('familyMemberName')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('familyMemberNamePlaceholder')}
                aria-label={t('familyMemberName')}
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="conditionCode">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('relatedCondition')}
              placeholder={t('relatedConditionPlaceholder')}
              items={conditionCode.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={conditionCode.onQueryChange}
              isLoading={conditionCode.isFetching}
              error={conditionCode.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <form.Field name="notes">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('notes')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('notePlaceholder')}
                aria-label={t('notes')}
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating}
              className="flex-1"
              aria-label={t('saveNewFamilyMember')}
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddFamilyMember')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
